import { allPartners, toolsForPartner } from '../game/partners';
import type { ToolId } from '../game/partners';
import { SOLVED_AT } from '../game/progress';
import type { Progress } from '../game/progress';
import { levelsForTool, toolProgress } from '../scenarios';
import type { Player } from '../game/identity';
import { play } from '../game/sound';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';
import { SiteFooter } from './SiteFooter';

interface Props {
  progress: Progress;
  onPick: (toolId: ToolId) => void;
  onBack: () => void;
  /** Who is signed in, if anyone. Null is a normal, playable state. */
  player: Player | null;
  onSignIn: () => void;
  onSignOut: () => void;
  /** Present only for a session the server confirmed may edit content. */
  onAdmin?: (() => void) | undefined;
}

/**
 * Choose a tool.
 *
 * Grouped by the company that makes each product, because that is the axis the
 * campaign grows along: a vendor with two products needs somewhere to put the
 * second one, and a flat list of tools would have no way to say they are
 * related. Today every vendor has exactly one product, so the grouping is
 * almost invisible — which is the point. It costs nothing now and it is already
 * right when it stops being free.
 *
 * Only tools that have levels are listed, and a vendor disappears with its
 * last playable tool. The menu used to show the empty ones as "In preparation"
 * so that someone who works in ClearCase could see it was coming; in practice
 * that filled the picker with four products nobody could play and named
 * vendors whose clearance is still open. Availability is derived from the
 * levels that exist, so a tool returns here the moment it has one.
 */
export function ToolPicker({ progress, onPick, onBack, onAdmin, player, onSignIn, onSignOut }: Props) {
  return (
    <div className="panel panel--menu">
      <RTNav />
      <div className="panel__inner">
        <BrandHeader />

        <p className="case-eyebrow">Choose your ground</p>
        <h1>Which tool?</h1>
        <p className="lede">
          Each set is a different product, and a different way of getting things
          quietly wrong.
        </p>

        {/*
          Sign-in is not required to browse, so this is the only place someone
          can tell whether the game knows who they are — and the only way to
          say so on purpose rather than by starting a level.
        */}
        <p className="identity-bar">
          {player ? (
            <>
              <span>
                Signed in as <strong>{player.name}</strong> ({player.email})
              </span>
              <button type="button" onClick={onSignOut}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <span>Not signed in — scores are not being saved.</span>
              <button type="button" onClick={onSignIn}>
                Sign in
              </button>
            </>
          )}
        </p>

        {allPartners().map((partner) => {
          // A vendor is shown only for the tools that are actually playable, so
          // one whose products all lack levels drops out entirely rather than
          // leaving a heading with nothing under it.
          const tools = toolsForPartner(partner.id).filter(
            (tool) => toolProgress(tool.id).available,
          );
          if (tools.length === 0) return null;

          return (
            <section key={partner.id} className="vendor">
              <h2 className="vendor__head">
                <span
                  className="vendor__rule"
                  style={{ background: partner.accent }}
                  aria-hidden="true"
                />
                {partner.name}
              </h2>

              {tools.map((tool) => {
                const { ready, target } = toolProgress(tool.id);
                const closed = levelsForTool(tool.id).filter(
                  (level) => (progress[level.id]?.bestScore ?? 0) >= SOLVED_AT,
                ).length;

                return (
                  <button
                    key={tool.id}
                    type="button"
                    className="tool-card"
                    style={{ borderLeftColor: partner.accent }}
                    onClick={() => {
                      play('select');
                      onPick(tool.id);
                    }}
                  >
                    <span className="tool-card__name">{tool.short}</span>
                    <span className="tool-card__blurb">{tool.blurb}</span>
                    <span className="tool-card__state">
                      {ready} of {target} cases ready
                      {closed > 0 && (
                        <span className="tool-card__closed"> · {closed} closed</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </section>
          );
        })}

        <button
          type="button"
          className="btn btn--ghost btn--wide"
          style={{ marginTop: 24 }}
          onClick={onBack}
        >
          Back
        </button>

        {onAdmin && (
          <button
            type="button"
            className="btn btn--ghost btn--wide admin-entry"
            onClick={onAdmin}
          >
            Level admin
          </button>
        )}

        <SiteFooter />
      </div>
    </div>
  );
}
